<script setup lang="ts">
import { liveStreamService } from '@/api/services/live-stream-service'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { LiveStream } from '@/types/live-stream.types'
import { computed, onMounted, ref } from 'vue'
import LiveStreamingListItem from './LiveStreamingListItem.vue'

const isMobile = useIsMobile()

const liveStreamingList = ref<LiveStream[]>([])

const emptyText = computed(() => {
  return isMobile.value
    ? '진행 중이거나 예정된\n라이브 스트리밍이 없습니다.'
    : '진행 중이거나 예정된 라이브 스트리밍이 없습니다.'
})

onMounted(async () => {
  const liveStreams = await liveStreamService.getLiveStreams()
  liveStreamingList.value = liveStreams
})
</script>

<template>
  <div class="live-streaming-list">
    <LiveStreamingListItem v-for="item in liveStreamingList" :key="item.idx" :item="item" />

    <!-- 결과가 없는 경우 -->
    <div v-if="liveStreamingList.length === 0" class="empty-state">
      <div class="empty-state-container">
        <div class="empty-state-icon-image">
          <img src="@/assets/svgs/cloud.svg" alt="empty-state-icon" />
        </div>
        <div class="empty-state-icon-text">
          <div class="title-sm white" :class="{ 'body-md': isMobile }">{{ emptyText }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.live-streaming-list {
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-top: 28px;
  align-items: center;
}

.list-item:hover {
  transform: scale(1.24);
  z-index: 100;
  border: 1px solid var(--tint-300);
  cursor: pointer;
}

.free-class-list:has(.list-item:hover) .list-item:not(:hover) {
  opacity: 0.2;
  transition: opacity 0.1s ease;
}

.empty-state {
  height: 100%;
  grid-column: 1 / -1;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 720px;
  max-height: 720px;
}

.empty-state-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-state-icon-image {
  width: 80px;
  height: 80px;
}
.empty-state-icon-image img {
  width: 100%;
  height: 100%;
}

.empty-state-icon-text {
  margin-top: 12px;
}

.empty-state-icon-text div {
  white-space: pre-line;
}

@media (max-width: 1365px) {
  .live-streaming-list {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 0px;
  }

  .list-item:hover {
    transform: none;
    border: none;
  }

  .live-streaming-list:has(.list-item:hover) .list-item:not(:hover) {
    opacity: 1;
    transition: none;
  }
}
</style>
